import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { FiAlertCircle, FiClock, FiHelpCircle, FiRefreshCw, FiSave, FiX } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingOverlay from "../../components/LoadingOverlay";
import TimesheetTable from "../../components/timesheet/TimesheetTable";
import TimesheetToolbar from "../../components/timesheet/TimesheetToolbar";
import TimesheetSubmissionCard from "../../components/timesheet/TimesheetSubmissionCard";
import TimesheetPeriodStatusCard from "../../components/timesheet/TimesheetPeriodStatusCard";
import {
  archiveTimesheetProject,
  createTimesheetProject,
  listTimesheetClients,
  updateTimesheetProject,
  fetchTimesheetOverview,
  saveTimesheetEntriesBatch,
  submitTimesheetWeek,
  withdrawTimesheetSubmission,
} from "../../services/timesheetApi";
import {
  buildEntryCellKey,
  TIMESHEET_VIEW,
  buildEntryLookup,
  formatHours,
  getEntityId,
  getRangeForView,
  normalizeDateKey,
  normalizeHoursInput,
  isValidHoursValue,
  shiftAnchorDate,
} from "../../utils/timesheetUtils";
import {
  readTimesheetCache,
  writeTimesheetCache,
} from "../../utils/timesheetCache";

const DUPLICATE_MESSAGE =
  "รายการนี้มีอยู่แล้ว กรุณาใช้ชื่ออื่น";

const TIMESHEET_CLIENTS_CACHE_KEY = "timesheetClients";
const TIMESHEET_CLIENTS_CACHE_TTL = 60 * 60 * 1000;
const TIMESHEET_OVERVIEW_CACHE_SCOPE = "overview";
const isClearedHoursValue = (hours) =>
  hours === "" || Number(hours) === 0;

const getCreatedEntity = (payload, key) =>
  payload?.[key] || payload?.data?.[key] || payload?.data || payload;

const normalizeProjectOptions = (projects = []) => {
  const seen = new Set();

  return (Array.isArray(projects) ? projects : []).reduce((options, project) => {
    const name = String(project || "").trim();
    const normalizedName = name.toLocaleLowerCase();
    if (!name || seen.has(normalizedName)) return options;
    seen.add(normalizedName);
    options.push(name);
    return options;
  }, []);
};

// ?week=YYYY-MM-DD (เช่นจากแจ้งเตือนปลดล็อก) ให้เปิดสัปดาห์นั้น
const parseWeekParam = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const TimesheetPage = () => {
  const [searchParams] = useSearchParams();
  const weekParam = searchParams.get("week") || searchParams.get("periodStart");
  const [viewMode, setViewMode] = useState(TIMESHEET_VIEW.WEEK);
  const [anchorDate, setAnchorDate] = useState(
    () => parseWeekParam(weekParam) || new Date()
  );

  useEffect(() => {
    const weekDate = parseWeekParam(weekParam);
    if (!weekDate) return;
    setViewMode(TIMESHEET_VIEW.WEEK);
    setAnchorDate(weekDate);
  }, [weekParam]);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [summary, setSummary] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [cellStatuses, setCellStatuses] = useState({});
  const [draftEntries, setDraftEntries] = useState({});
  const [pendingRows, setPendingRows] = useState([]);
  const [savingChanges, setSavingChanges] = useState(false);
  const [projectActionLoading, setProjectActionLoading] = useState(false);
  const [weekSubmission, setWeekSubmission] = useState(null);
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [periodStatus, setPeriodStatus] = useState(null);
  const [periodStatusLoading, setPeriodStatusLoading] = useState(false);
  const [periodStatusError, setPeriodStatusError] = useState("");
  const [showGuide, setShowGuide] = useState(false);
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);

  const statusTimeoutsRef = useRef({});
  const entriesLookupRef = useRef({});
  const rangeRequestSequenceRef = useRef(0);

  const range = useMemo(
    () => getRangeForView(viewMode, anchorDate),
    [anchorDate, viewMode]
  );
  const draftCount = Object.keys(draftEntries).length + pendingRows.length;
  const overlayLoading =
    pageLoading ||
    clientsLoading ||
    summaryLoading ||
    savingChanges ||
    projectActionLoading ||
    submitting ||
    withdrawing;
  const projectOptionsByClient = useMemo(
    () =>
      Object.fromEntries(
        clients.map((client) => [
          getEntityId(client._id || client.id),
          normalizeProjectOptions(client.projects),
        ])
      ),
    [clients]
  );

  useEffect(() => {
    setDraftEntries({});
    setPendingRows([]);
    setCellStatuses({});
  }, [range.from, range.to, viewMode]);

  // approved ไม่ได้แปลว่าแก้ไม่ได้เสมอ (อาจถูกปลดล็อก) ให้ period-status เป็นตัวตัดสิน
  const submissionLocked =
    viewMode === TIMESHEET_VIEW.WEEK &&
    String(weekSubmission?.status || "").toLowerCase() === "pending";
  const weekLocked =
    viewMode === TIMESHEET_VIEW.WEEK &&
    (submissionLocked || periodStatus?.canEdit !== true);

  const markCellStatus = useCallback((cellKey, status) => {
    setCellStatuses((prev) => {
      if (!status) {
        const next = { ...prev };
        delete next[cellKey];
        return next;
      }

      return { ...prev, [cellKey]: status };
    });

    if (statusTimeoutsRef.current[cellKey]) {
      clearTimeout(statusTimeoutsRef.current[cellKey]);
    }

    if (status === "saved") {
      statusTimeoutsRef.current[cellKey] = setTimeout(() => {
        setCellStatuses((prev) => {
          const next = { ...prev };
          delete next[cellKey];
          return next;
        });
      }, 1800);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    let cachedClients = null;

    try {
      const cached = localStorage.getItem(TIMESHEET_CLIENTS_CACHE_KEY);
      const parsed = cached ? JSON.parse(cached) : null;
      const cachedAt = Number(parsed?.cachedAt);
      const now = Date.now();
      const cacheIsFresh =
        Number.isFinite(cachedAt) &&
        cachedAt <= now &&
        now - cachedAt < TIMESHEET_CLIENTS_CACHE_TTL;

      cachedClients = cacheIsFresh && Array.isArray(parsed?.clients)
        ? parsed.clients
        : null;

      if (cached && !cachedClients) {
        localStorage.removeItem(TIMESHEET_CLIENTS_CACHE_KEY);
      }
    } catch (error) {
      localStorage.removeItem(TIMESHEET_CLIENTS_CACHE_KEY);
    }

    if (cachedClients) setClients(cachedClients);

    try {
      setClientsLoading(!cachedClients);
      const data = await listTimesheetClients();
      const clientList = Array.isArray(data) ? data : [];
      setClients(clientList);
      localStorage.setItem(
        TIMESHEET_CLIENTS_CACHE_KEY,
        JSON.stringify({ clients: clientList, cachedAt: Date.now() })
      );
    } catch (error) {
      console.error("Failed to load timesheet clients:", error);
      if (!cachedClients) {
        toast.error(
          error?.response?.data?.message || "ไม่สามารถโหลดรายชื่อลูกค้าได้"
        );
      }
    } finally {
      setClientsLoading(false);
    }
  }, []);

  const applyOverviewData = useCallback((overview) => {
    const entries = Array.isArray(overview?.entries) ? overview.entries : [];
    const submissions = Array.isArray(overview?.submissions)
      ? overview.submissions
      : [];
    const periodStatuses = Array.isArray(overview?.periodStatuses)
      ? overview.periodStatuses
      : [];

    setSummary(
      overview?.summary || { range: overview?.range || range, totalHours: 0, clients: [] }
    );
    entriesLookupRef.current = buildEntryLookup(entries);

    if (viewMode === TIMESHEET_VIEW.WEEK) {
      const matchingSubmissions = submissions
        .filter(
          (item) =>
            String(item?.periodType || "week").toLowerCase() === "week" &&
            normalizeDateKey(item?.periodStart) === range.from &&
            normalizeDateKey(item?.periodEnd) === range.to
        )
        .sort(
          (a, b) =>
            new Date(b?.submittedAt || b?.createdAt || 0).getTime() -
            new Date(a?.submittedAt || a?.createdAt || 0).getTime()
        );
      const matchingPeriodStatus = periodStatuses.find((status) =>
        [status?.periodStart, status?.from, status?.range?.from]
          .map(normalizeDateKey)
          .includes(range.from)
      );

      setSubmissionHistory(matchingSubmissions);
      setWeekSubmission(matchingSubmissions[0] || null);
      setPeriodStatus(matchingPeriodStatus || periodStatuses[0] || null);
    } else {
      setSubmissionHistory([]);
      setWeekSubmission(null);
      setPeriodStatus(null);
    }
  }, [range, viewMode]);

  const fetchOverviewData = useCallback(async () => {
    const requestSequence = rangeRequestSequenceRef.current + 1;
    rangeRequestSequenceRef.current = requestSequence;
    const cacheKey = { from: range.from, to: range.to, viewMode };
    const cachedOverview = readTimesheetCache(
      TIMESHEET_OVERVIEW_CACHE_SCOPE,
      cacheKey,
    );

    if (cachedOverview) {
      applyOverviewData(cachedOverview);
      setPageLoading(false);
      setSummaryLoading(false);
      setSubmissionLoading(false);
      setPeriodStatusLoading(false);
      setPeriodStatusError("");
      setBackgroundRefreshing(true);
    }

    try {
      setSummaryLoading(!cachedOverview);
      setSubmissionLoading(!cachedOverview && viewMode === TIMESHEET_VIEW.WEEK);
      setPeriodStatusLoading(!cachedOverview && viewMode === TIMESHEET_VIEW.WEEK);
      setPeriodStatusError("");

      const overview = await fetchTimesheetOverview({
        from: range.from,
        to: range.to,
      });

      if (rangeRequestSequenceRef.current !== requestSequence) return;
      applyOverviewData(overview);
      writeTimesheetCache(TIMESHEET_OVERVIEW_CACHE_SCOPE, cacheKey, overview);
    } catch (error) {
      if (rangeRequestSequenceRef.current !== requestSequence) return;

      console.error("Failed to load timesheet overview:", error);
      if (!cachedOverview) {
        toast.error(
          error?.response?.data?.message || "ไม่สามารถโหลดข้อมูลเวลาทำงานได้"
        );
        setSummary({ range, totalHours: 0, clients: [] });
        entriesLookupRef.current = {};
        setSubmissionHistory([]);
        setWeekSubmission(null);
        setPeriodStatus(null);
        setPeriodStatusError(
          error?.response?.status === 403
            ? "คุณไม่มีสิทธิ์ดูสถานะช่วงเวลานี้"
            : "ไม่สามารถตรวจสอบกำหนดส่ง Timesheet ได้"
        );
      }
    } finally {
      if (rangeRequestSequenceRef.current === requestSequence) {
        setSummaryLoading(false);
        setSubmissionLoading(false);
        setPeriodStatusLoading(false);
        setPageLoading(false);
        setBackgroundRefreshing(false);
      }
    }
  }, [applyOverviewData, range, viewMode]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    fetchOverviewData();
  }, [fetchOverviewData]);

  useEffect(
    () => () => {
      Object.values(statusTimeoutsRef.current).forEach((timeoutId) =>
        clearTimeout(timeoutId)
      );
    },
    []
  );

  const refreshAll = useCallback(async () => {
    await fetchOverviewData();
  }, [fetchOverviewData]);

  const rows = useMemo(() => {
    const summaryRows = (summary?.clients || []).map((client) => {
      const clientId = getEntityId(client.clientId);
      return {
        ...client,
        clientId,
        projects: (client.projects || []).map((project) => ({
            ...project,
          clientId,
        })),
      };
    });

    if (!selectedClientId) return summaryRows;

    return [...summaryRows].sort((firstClient, secondClient) => {
      if (firstClient.clientId === selectedClientId) return -1;
      if (secondClient.clientId === selectedClientId) return 1;
      return 0;
    });
  }, [selectedClientId, summary]);

  const handleAddPendingRow = useCallback(() => {
    setPendingRows((previous) => [
      ...previous,
      {
        tempId: `pending-row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        clientId: "",
        projectName: "",
        projectId: "",
        hours: {},
        lockClient: false,
      },
    ]);
  }, []);

  const handleAddPendingProjectRow = useCallback((clientId) => {
    if (!clientId) {
      toast.info("กรุณาเพิ่มและเลือกลูกค้าก่อนเพิ่ม Project");
      return;
    }

    setSelectedClientId(clientId);
    setPendingRows((previous) => [
      ...previous,
      {
        tempId: `pending-project-row-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 7)}`,
        clientId,
        projectName: "",
        projectId: "",
        hours: {},
        lockClient: true,
      },
    ]);
  }, []);

  const handlePendingClientChange = useCallback((tempId, clientId) => {
    setPendingRows((previous) =>
      previous.map((row) =>
        row.tempId === tempId
          ? { ...row, clientId, projectName: "", projectId: "", hours: {} }
          : row
      )
    );
    setSelectedClientId(clientId);
  }, []);

  const handlePendingProjectChange = useCallback((tempId, projectName) => {
    setPendingRows((previous) =>
      previous.map((row) =>
        row.tempId === tempId
          ? { ...row, projectName, projectId: "", hours: {} }
          : row
      )
    );
  }, []);

  const handlePendingHoursChange = useCallback((tempId, workDate, hours) => {
    const normalizedHours = normalizeHoursInput(hours);
    setPendingRows((previous) =>
      previous.map((row) =>
        row.tempId === tempId
          ? {
              ...row,
              hours: {
                ...row.hours,
                [normalizeDateKey(workDate)]: normalizedHours,
              },
            }
          : row
      )
    );
  }, []);

  const handleRemovePendingRow = useCallback((tempId) => {
    setPendingRows((previous) =>
      previous.filter((row) => row.tempId !== tempId)
    );
  }, []);

  const handleEditProject = useCallback(
    async (project, projectName) => {
      setProjectActionLoading(true);
      try {
        await updateTimesheetProject(project.projectId, {
          name: String(projectName).trim(),
        });
        await fetchOverviewData();
        toast.success("แก้ไข Project สำเร็จ");
        return true;
      } catch (error) {
        console.error("Update project failed:", error);
        toast.error(
          error?.response?.status === 409
            ? DUPLICATE_MESSAGE
            : error?.response?.data?.message || "ไม่สามารถแก้ไข Project ได้"
        );
        return false;
      } finally {
        setProjectActionLoading(false);
      }
    },
    [fetchOverviewData]
  );

  const handleSaveProjectRemark = useCallback(
    async (project, remark) => {
      setProjectActionLoading(true);
      try {
        await updateTimesheetProject(project.projectId, {
          remark: String(remark || "").trim(),
        });
        await fetchOverviewData();
        toast.success(String(remark || "").trim() ? "บันทึก Remark แล้ว" : "ล้าง Remark แล้ว");
        return true;
      } catch (error) {
        console.error("Update project remark failed:", error);
        toast.error(error?.response?.data?.message || "ไม่สามารถบันทึก Remark ได้");
        return false;
      } finally {
        setProjectActionLoading(false);
      }
    },
    [fetchOverviewData]
  );

  const handleArchiveProject = useCallback(
    async (project) => {
      const result = await Swal.fire({
        icon: "warning",
        title: "ลบ Project นี้ใช่หรือไม่?",
        text: project.name,
        showCancelButton: true,
        confirmButtonText: "ลบ",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#dc2626",
      });
      if (!result.isConfirmed) return false;

      setProjectActionLoading(true);
      try {
        await archiveTimesheetProject(project.projectId);
        await fetchOverviewData();
        toast.success("ลบ Project แล้ว");
        return true;
      } catch (error) {
        console.error("Archive project failed:", error);
        toast.error(
          error?.response?.data?.message || "ไม่สามารถลบ Project ได้"
        );
        return false;
      } finally {
        setProjectActionLoading(false);
      }
    },
    [fetchOverviewData]
  );

  const handleDraftChange = useCallback(
    ({ clientId, projectId, workDate, hours }) => {
      const normalizedProjectId = getEntityId(projectId);
      const normalizedWorkDate = normalizeDateKey(workDate);
      const cellKey = buildEntryCellKey(normalizedProjectId, normalizedWorkDate);
      const normalizedHours = normalizeHoursInput(hours);
      const persistedHours = normalizeHoursInput(
        entriesLookupRef.current[cellKey]?.hours || ""
      );

      setDraftEntries((previous) => {
        const next = { ...previous };
        if (normalizedHours === persistedHours) {
          delete next[cellKey];
        } else {
          next[cellKey] = {
            cellKey,
            clientId: getEntityId(clientId),
            projectId: normalizedProjectId,
            workDate: normalizedWorkDate,
            hours: normalizedHours,
          };
        }
        return next;
      });
      markCellStatus(cellKey, normalizedHours === persistedHours ? "" : "draft");
    },
    [markCellStatus]
  );

  const handleSaveChanges = useCallback(async () => {
    if (weekLocked) {
      toast.error(
        periodStatus?.lockReason === "deadline"
          ? "หมดเวลาสำหรับแก้ไข Timesheet ช่วงนี้แล้ว"
          : "สัปดาห์ Timesheet นี้ถูกล็อก"
      );
      return;
    }

    const changes = Object.values(draftEntries);
    const pendingToSave = [...pendingRows];
    if (!changes.length && !pendingToSave.length) {
      toast.info("ยังไม่มีรายการที่แก้ไข");
      return;
    }

    const incompleteRow = pendingToSave.find(
      (row) => !row.clientId || !String(row.projectName || "").trim()
    );
    if (incompleteRow) {
      toast.error("กรุณาเลือก Client และ Project ให้ครบทุกแถว");
      return;
    }

    const allHourValues = [
      ...changes.map((change) => change.hours),
      ...pendingToSave.flatMap((row) => Object.values(row.hours || {})),
    ];
    if (allHourValues.some((hours) => !isValidHoursValue(hours))) {
      toast.error("กรุณาระบุชั่วโมงตั้งแต่ 0 และไม่เกิน 24 ชั่วโมง");
      return;
    }

    const pendingKeys = new Set();
    const hasDuplicatePendingRow = pendingToSave.some((row) => {
      const key = `${row.clientId}:${String(row.projectName).trim().toLocaleLowerCase()}`;
      if (pendingKeys.has(key)) return true;
      pendingKeys.add(key);
      return false;
    });
    if (hasDuplicatePendingRow) {
      toast.error("มี Client และ Project ซ้ำกันในรายการที่กำลังเพิ่ม");
      return;
    }

    const requestedOperationCount =
      changes.reduce((count, change) => {
        const existingEntry =
          entriesLookupRef.current[
            buildEntryCellKey(change.projectId, change.workDate)
          ];
        if (isClearedHoursValue(change.hours)) {
          return count + (existingEntry?.entryId ? 1 : 0);
        }
        return count + 1;
      }, 0) +
      pendingToSave.reduce(
        (count, row) =>
          count +
          Object.values(row.hours || {}).filter(
            (hours) => !isClearedHoursValue(hours)
          ).length,
        0
      );
    if (requestedOperationCount > 500) {
      toast.error("บันทึกได้สูงสุดครั้งละ 500 รายการ กรุณาแบ่งบันทึกข้อมูล");
      return;
    }

    try {
      setSavingChanges(true);
      const entries = [];
      const deleteEntryIds = [];

      changes.forEach((change) => {
        const cellKey = buildEntryCellKey(change.projectId, change.workDate);
        const existingEntry = entriesLookupRef.current[cellKey];

        if (isClearedHoursValue(change.hours)) {
          if (existingEntry?.entryId) {
            deleteEntryIds.push(existingEntry.entryId);
          }
          return;
        }

        entries.push({
          clientId: change.clientId,
          projectId: change.projectId,
          workDate: change.workDate,
          hours: Number(change.hours),
        });
      });

      for (const pendingRow of pendingToSave) {
        let projectId = pendingRow.projectId;

        if (!projectId) {
          const response = await createTimesheetProject({
            clientId: pendingRow.clientId,
            name: String(pendingRow.projectName).trim(),
          });
          const createdProject = getCreatedEntity(response, "project");
          projectId = getEntityId(
            createdProject?._id || createdProject?.id || createdProject?.projectId
          );
          if (!projectId) {
            throw new Error("Project created without an id");
          }

          setPendingRows((previous) =>
            previous.map((row) =>
              row.tempId === pendingRow.tempId ? { ...row, projectId } : row
            )
          );
        }

        const pendingEntries = Object.entries(pendingRow.hours || {})
          .filter(([, hours]) => !isClearedHoursValue(hours))
          .map(([workDate, hours]) => ({
            clientId: pendingRow.clientId,
            projectId,
            workDate,
            hours: Number(hours),
          }));
        entries.push(...pendingEntries);
      }

      if (entries.length || deleteEntryIds.length) {
        await saveTimesheetEntriesBatch({ entries, deleteEntryIds });
      }

      setDraftEntries({});
      const savedPendingIds = new Set(pendingToSave.map((row) => row.tempId));
      setPendingRows((previous) =>
        previous.filter((row) => !savedPendingIds.has(row.tempId))
      );
      changes.forEach((change) => markCellStatus(change.cellKey, "saved"));
      await fetchOverviewData();
      toast.success("บันทึกเวลาทำงานสำเร็จ");
    } catch (error) {
      console.error("Save timesheet changes failed:", error);
      if (error?.response?.status === 409) {
        await fetchOverviewData();
      }
      toast.error(
        error?.response?.data?.message || "ไม่สามารถบันทึกเวลาทำงานได้"
      );
    } finally {
      setSavingChanges(false);
    }
  }, [
    draftEntries,
    fetchOverviewData,
    markCellStatus,
    pendingRows,
    periodStatus,
    weekLocked,
  ]);

  const handleSubmitWeek = useCallback(async () => {
    if (periodStatus?.canSubmit !== true) {
      toast.error(
        periodStatus?.lockReason === "deadline"
          ? "หมดเวลาสำหรับส่ง Timesheet ช่วงนี้แล้ว"
          : "ช่วงเวลานี้ยังไม่สามารถส่ง Timesheet ได้"
      );
      await fetchOverviewData();
      return;
    }
    if (Number(summary?.totalHours || 0) <= 0) {
      toast.error("กรุณาบันทึกเวลาอย่างน้อย 1 ชั่วโมงก่อนส่ง Timesheet");
      return;
    }
    const result = await Swal.fire({
      icon: "question",
      title: "ส่ง Timesheet นี้เพื่อขออนุมัติหรือไม่?",
      text: "คุณจะไม่สามารถแก้ไขสัปดาห์นี้ได้ระหว่างรออนุมัติ",
      showCancelButton: true,
      confirmButtonText: "ส่ง Timesheet",
    });
    if (!result.isConfirmed) return;
    try {
      setSubmitting(true);
      await submitTimesheetWeek(range.from);
      await fetchOverviewData();
      toast.success("ส่ง Timesheet เพื่อขออนุมัติแล้ว");
    } catch (error) {
      const status = error?.response?.status;
      const lockReason = String(
        error?.response?.data?.lockReason || ""
      ).toLowerCase();
      const errorMessage = String(
        error?.response?.data?.message || ""
      ).toLowerCase();
      const deadlineError =
        lockReason === "deadline" || errorMessage.includes("deadline");
      if (status === 409) {
        await fetchOverviewData();
      }
      const message = status === 409 && deadlineError
        ? "หมดเวลาสำหรับส่ง Timesheet ช่วงนี้แล้ว"
        : status === 409
        ? "ช่วงเวลานี้ถูกล็อก ไม่สามารถส่ง Timesheet ได้"
        : status === 422
        ? "ยังไม่ได้ตั้งค่า approval flow สำหรับบัญชีของคุณ"
        : error?.response?.data?.message || "ไม่สามารถส่ง Timesheet ได้";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }, [fetchOverviewData, periodStatus, range.from, summary?.totalHours]);

  const handleWithdrawWeek = useCallback(async () => {
    if (!weekSubmission?._id && !weekSubmission?.id) return;
    const result = await Swal.fire({
      icon: "warning",
      title: "ต้องการถอนการส่งนี้หรือไม่?",
      text: "สัปดาห์นี้จะกลับมาแก้ไขได้อีกครั้ง",
      showCancelButton: true,
      confirmButtonText: "ถอนการส่ง",
      confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;
    try {
      setWithdrawing(true);
      await withdrawTimesheetSubmission(weekSubmission._id || weekSubmission.id);
      await fetchOverviewData();
      toast.success("ถอนการส่ง Timesheet แล้ว");
    } catch (error) {
      toast.error(error?.response?.status === 409 ? "ไม่สามารถถอนการส่งนี้ได้อีกแล้ว" : error?.response?.data?.message || "ไม่สามารถถอนการส่งได้");
    } finally {
      setWithdrawing(false);
    }
  }, [fetchOverviewData, weekSubmission]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 sm:px-6 py-5 transition-colors duration-300">
      <LoadingOverlay loading={overlayLoading} />

      <div className="rounded-3xl border border-indigo-100 dark:border-indigo-900/40 bg-gradient-to-br from-white via-indigo-50/50 to-sky-50/70 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 p-6 sm:p-8 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300">
              <FiClock size={22} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100">
                Timesheet
              </h1>
              <p className="mt-2 max-w-2xl text-sm sm:text-base text-gray-600 dark:text-gray-400">
                บันทึกชั่วโมงการทำงาน แยกตามลูกค้าและ Project
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={refreshAll}
              disabled={summaryLoading || backgroundRefreshing}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-indigo-300 dark:hover:border-indigo-700 transition disabled:opacity-60"
            >
              <FiRefreshCw
                className={summaryLoading || backgroundRefreshing ? "animate-spin" : ""}
              />
              รีเฟรช
            </button>
          </div>
        </div>

      </div>

      <div className={`mt-6 grid gap-6 ${draftCount ? "pb-28 sm:pb-32" : ""}`}>
        <TimesheetToolbar
          viewMode={viewMode}
          rangeLabel={range.label}
          days={range.days}
          totalHours={formatHours(summary?.totalHours || 0)}
          onViewChange={setViewMode}
          onPrevious={() =>
            setAnchorDate((current) => shiftAnchorDate(viewMode, current, -1))
          }
          onNext={() =>
            setAnchorDate((current) => shiftAnchorDate(viewMode, current, 1))
          }
        />

        {viewMode === TIMESHEET_VIEW.WEEK && (
          <>
            <TimesheetPeriodStatusCard
              range={range}
              status={periodStatus}
              submission={weekSubmission}
              loading={periodStatusLoading}
              error={periodStatusError}
            />
            <TimesheetSubmissionCard
              submission={weekSubmission}
              loading={submissionLoading}
              totalHours={formatHours(summary?.totalHours || 0)}
              submitting={submitting}
              withdrawing={withdrawing}
              history={submissionHistory}
              periodCanSubmit={periodStatus?.canSubmit === true}
              periodCanEdit={periodStatus?.canEdit === true}
              onSubmit={handleSubmitWeek}
              onWithdraw={handleWithdrawWeek}
            />
          </>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowGuide((current) => !current)}
            aria-expanded={showGuide}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-700 transition hover:bg-indigo-50 dark:border-indigo-900/60 dark:bg-gray-800 dark:text-indigo-300"
          >
            <FiHelpCircle size={17} />
            วิธีใช้งาน
          </button>
        </div>

        {showGuide && (
          <div className="rounded-2xl border border-indigo-200 bg-white p-4 shadow-sm dark:border-indigo-900/60 dark:bg-gray-800 sm:p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">วิธีบันทึก Timesheet</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">ทำตามขั้นตอนนี้เพื่อเริ่มบันทึกเวลาทำงาน</p>
              </div>
              <button type="button" onClick={() => setShowGuide(false)} aria-label="ปิดวิธีใช้งาน" className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"><FiX size={18} /></button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {[
                { number: "1", title: "เพิ่มลูกค้า", text: "กด เพิ่มลูกค้า เพื่อสร้างแถวใหม่ในตาราง", tone: "border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-900/60 dark:bg-sky-500/10 dark:text-sky-200" },
                { number: "2", title: "เลือกข้อมูล", text: "เลือก Client และ Project ให้ครบในแต่ละแถว", tone: "border-violet-200 bg-violet-50 text-violet-800 dark:border-violet-900/60 dark:bg-violet-500/10 dark:text-violet-200" },
                { number: "3", title: "กรอกชั่วโมง", text: "กรอกชั่วโมงของ Project ในแต่ละวันได้โดยตรง", tone: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-500/10 dark:text-amber-200" },
                { number: "4", title: "บันทึก Timesheet", text: "กด บันทึก Timesheet จากแถบด้านล่างหน้าจอ", tone: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-500/10 dark:text-emerald-200" },
              ].map((step) => (
                <div key={step.number} className={`rounded-xl border p-3 ${step.tone}`}>
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/80 text-xs font-bold shadow-sm dark:bg-gray-900/40">{step.number}</span>
                  <p className="mt-2 text-sm font-semibold">{step.title}</p>
                  <p className="mt-1 text-xs leading-5 opacity-90">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {summaryLoading ? (
          <div className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-14 text-center shadow-sm">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              กำลังโหลดข้อมูลเวลาทำงาน...
            </p>
          </div>
        ) : (
          <>
            <TimesheetTable
              viewMode={viewMode}
              days={range.days}
              rows={rows}
              clients={clients}
              clientsLoading={clientsLoading}
              selectedClientId={selectedClientId}
              pendingRows={pendingRows}
              cellStatuses={cellStatuses}
              draftEntries={draftEntries}
              projectOptionsByClient={projectOptionsByClient}
              saving={savingChanges}
              locked={weekLocked}
              emptyMessage="เลือกลูกค้าและเพิ่ม Project เพื่อเริ่มบันทึกเวลาทำงาน"
              onAddPendingRow={handleAddPendingRow}
              onAddPendingProjectRow={handleAddPendingProjectRow}
              onPendingClientChange={handlePendingClientChange}
              onPendingProjectChange={handlePendingProjectChange}
              onPendingHoursChange={handlePendingHoursChange}
              onRemovePendingRow={handleRemovePendingRow}
              onEditProject={handleEditProject}
              onSaveRemark={handleSaveProjectRemark}
              onArchiveProject={handleArchiveProject}
              onDraftChange={handleDraftChange}
            />

            {draftCount > 0 && (
              <div className="fixed inset-x-3 bottom-3 z-50 md:left-72 md:right-6">
                <div className="mx-auto flex max-w-[1600px] flex-col gap-3 rounded-2xl border-2 border-amber-300 bg-amber-50/95 p-4 shadow-lg backdrop-blur dark:border-amber-500/60 dark:bg-gray-900/95 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <FiAlertCircle className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-300" size={20} />
                    <div>
                      <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                        ยังไม่ได้บันทึก {draftCount} รายการ
                      </p>
                      <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                        กรุณากดบันทึก Timesheet ก่อนออกจากหน้านี้
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveChanges}
                    disabled={savingChanges || weekLocked}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-600 focus:outline-none focus:ring-4 focus:ring-amber-200 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:ring-amber-500/20 sm:w-auto"
                  >
                    <FiSave className={savingChanges ? "animate-pulse" : ""} />
                    {savingChanges ? "กำลังบันทึก..." : "บันทึก Timesheet"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TimesheetPage;
